// src/services/auditLogsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export interface AuditLog {
  id: string
  businessId: string
  userId?: string
  userName?: string
  userEmail?: string
  action: string
  resourceType: string
  resourceId?: string
  resourceName?: string
  oldValues?: any
  newValues?: any
  changedFields?: string[]
  ipAddress: string
  userAgent?: string
  requestId?: string
  sessionId?: string
  success: boolean
  errorCode?: string
  errorMessage?: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  tags: string[]
  metadata: {
    endpoint?: string
    method?: string
    responseTime?: number
    deviceInfo?: {
      device_type?: string
      device_model?: string
      os_version?: string
      app_version?: string
    }
    securityEvent?: boolean
    gdprRelevant?: boolean
    [key: string]: any
  }
  isPersonalData: boolean
  retentionDate?: string
  createdAt: string
  updatedAt: string
}

export interface AuditLogResponse {
  status: 'success' | 'error'
  message: string
  data: {
    logs: AuditLog[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
      hasNextPage: boolean
      hasPrevPage: boolean
    }
  }
}

export interface AuditLogStatsResponse {
  status: 'success' | 'error'
  message: string
  data: {
    period: string
    totalLogs: number
    uniqueUsers: number
    successfulActions: number
    failedActions: number
    severityBreakdown: {
      low: number
      medium: number
      high: number
      critical: number
    }
    actionBreakdown: {
      [action: string]: number
    }
  }
}

export interface AuditLogFilters {
  businessId: string
  userId?: string
  action?: string
  resourceType?: string
  severity?: string
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
}

export interface SecurityEventsResponse {
  status: 'success' | 'error'
  message: string
  data: {
    logs: AuditLog[]
    period: string
    total: number
    criticalCount: number
    highCount: number
  }
}

export interface UserActivityResponse {
  status: 'success' | 'error'
  message: string
  data: {
    logs: AuditLog[]
    summary: {
      totalActions: number
      loginCount: number
      dataModifications: number
      failedAttempts: number
    }
    period: string
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }
}

export const auditLogsApi = createApi({
  reducerPath: 'auditLogsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/audit-logs`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['AuditLogs', 'AuditStats'],
  endpoints: (builder) => ({
    // Get audit logs with filters
    getAuditLogs: builder.query<AuditLogResponse, AuditLogFilters>({
      query: (params) => ({
        url: '',
        params: {
          businessId: params.businessId,
          userId: params.userId,
          action: params.action,
          resourceType: params.resourceType,
          severity: params.severity,
          startDate: params.startDate,
          endDate: params.endDate,
          page: params.page || 1,
          limit: params.limit || 10
        }
      }),
      providesTags: ['AuditLogs']
    }),

    // Get audit log statistics
    getAuditLogStats: builder.query<AuditLogStatsResponse, { businessId: string; days?: number }>({
      query: ({ businessId, days = 30 }) => ({
        url: '/stats',
        params: { businessId, days }
      }),
      providesTags: ['AuditStats']
    }),

    // Get recent audit logs
    getRecentAuditLogs: builder.query<AuditLogResponse, { businessId: string; limit?: number }>({
      query: ({ businessId, limit = 20 }) => ({
        url: '/recent',
        params: { businessId, limit }
      }),
      providesTags: ['AuditLogs']
    }),

    // Get security events
    getSecurityEvents: builder.query<SecurityEventsResponse, { 
      businessId: string; 
      days?: number; 
      page?: number; 
      limit?: number 
    }>({
      query: ({ businessId, days = 7, page = 1, limit = 20 }) => ({
        url: '/security-events',
        params: { businessId, days, page, limit }
      }),
      providesTags: ['AuditLogs']
    }),

    // Get user activity
    getUserActivity: builder.query<UserActivityResponse, {
      businessId: string;
      userId: string;
      days?: number;
      page?: number;
      limit?: number;
    }>({
      query: ({ businessId, userId, days = 30, page = 1, limit = 20 }) => ({
        url: '/user-activity',
        params: { businessId, userId, days, page, limit }
      }),
      providesTags: ['AuditLogs']
    }),

    // Export audit logs
    exportAuditLogs: builder.mutation<Blob, {
      businessId: string;
      startDate?: string;
      endDate?: string;
      action?: string;
      severity?: string;
    }>({
      query: ({ businessId, startDate, endDate, action, severity }) => ({
        url: '/export',
        params: {
          businessId,
          startDate,
          endDate,
          action,
          severity
        },
        responseHandler: (response) => response.blob(),
      })
    })
  })
})

export const {
  useGetAuditLogsQuery,
  useGetAuditLogStatsQuery,
  useGetRecentAuditLogsQuery,
  useGetSecurityEventsQuery,
  useGetUserActivityQuery,
  useExportAuditLogsMutation
} = auditLogsApi